import AlertMessages from "common/Alert/AlertMessages";
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { useGetSubscriptionPlanDropdownService } from "subModule/src/services/subscriptionplans/useSubscriptionPlan";
import Loader from "layout/Loader";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import StyledInput from "common/SearchField/Search";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { brandingInfo } from "features/server/brandingSlice";
import { themes } from "theme/themes";
import { useParams } from "react-router-dom";
import { SubscriptionPlanProfileService } from "subModule/src/core/services";
interface SubscriptionPlanChangeProps {
  setChangePlanModal: (value: boolean) => void;
  planRefetch: () => void;
  subscriptionPlanId?: number | null;
  businessId?: number | null;
}
interface FormData {
  [key: string]: any;
}
export const SubscriptionPlanChange: React.FC<SubscriptionPlanChangeProps> = ({
  setChangePlanModal,
  planRefetch,
  subscriptionPlanId,
  businessId,
}) => {
  const params = useParams();
  const resolvedBusinessId = businessId ?? Number(params?.id);
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const [searchTerm, setSearchTerm] = useState<any>("");
  const [search, setSearch] = useState("");
  const {
    data: tableData,
    isFetching,
    refetch,
  } = searchTerm
    ? useGetSubscriptionPlanDropdownService({
        pageNumber: 1,
        size: 100,
        name: searchTerm,
      })
    : useGetSubscriptionPlanDropdownService({ pageNumber: 1, size: 100 });
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: { planId: subscriptionPlanId },
  });
  const fetchData = useCallback(() => {
    refetch().then((res: any) => {
      if (!res?.data?.pages[0]?.success)
        enqueueSnackbar(
          <AlertMessages
            text={res?.data?.pages[0]?.message || "Something went wrong"}
          />,
          { variant: "error" },
        );
    });
  }, []);
  useEffect(() => {
    fetchData();
  }, [searchTerm]);
  const clearSearch = () => {
    setSearch("");
    setSearchTerm("");
    if (searchTerm) {
      handleSearch("");
    }
  };
  const handleSearch = (search: string) => {
    setSearchTerm(search);
  };
  const handleSearchClick = () => {
    const trimmedSearch = search.trim();
    if (trimmedSearch !== "") {
      handleSearch(trimmedSearch);
    }
  };
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value.trim());
    if (!event.target.value.trim()) handleSearch("");
  };
  const onSubmit = (data: FormData) => {
    SubscriptionPlanProfileService.changeSubscriptionPlan({
      businessId: Number(resolvedBusinessId),
      planId: data?.planId,
    })
      .then((response: any) => {
        if (response.success) {
          enqueueSnackbar(
            <AlertMessages
              statusCode={response.status}
              text={t("state") + " " + t("updated") + " " + t("successfully")}
            />,
            { variant: "success" },
          );
          setChangePlanModal(false);
          planRefetch();
        } else {
          enqueueSnackbar(<AlertMessages text={response?.message} />, {
            variant: "error",
          });
        }
      })
      .catch((err: any) => {
        console.error("===", err);
        enqueueSnackbar(
          <AlertMessages
            text={err?.message || err?.data?.message || "Something went wrong"}
          />,
          { variant: "error" },
        );
      });
  };
  return (
    <Box>
      {isFetching ? (
        <Loader />
      ) : (
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          className="mt-[10px]"
        >
          <Box className="mb-4">
            <StyledInput
              fullWidth
              placeholder="subscriptionPlans"
              handleSearchClick={handleSearchClick}
              handleChange={handleInputChange}
              value={search}
              clearSearch={clearSearch}
            />
          </Box>
          <Controller
            name="planId"
            control={control}
            rules={{ required: t("This field is required.") }}
            render={({ field }) => (
              <Box className="h-[200px] overflow-y-auto">
                <RadioGroup
                  {...field}
                  value={field.value ?? ""}
                  onChange={(e) => field.onChange(Number(e.target.value))}
                >
                  {(tableData?.pages[0]?.data?.content || [])?.map(
                    (item: any) => (
                      <Box key={item.id}>
                        <FormControlLabel
                          value={item.id}
                          control={
                            <Radio
                              sx={{
                                color: "#343434",
                                "&.Mui-checked": {
                                  color: themeColors?.primary,
                                },
                                "& svg:nth-child(2) path": {
                                  fill: themeColors?.primary,
                                },
                              }}
                            />
                          }
                          label={
                            <Typography className="font-Saira font-[500] text-[11px] text-[#6B6B6B]">
                              {item.name}
                            </Typography>
                          }
                          className="mx-0 w-full py-1"
                        />
                        <Divider />
                      </Box>
                    ),
                  )}
                </RadioGroup>
              </Box>
            )}
          />
          {errors?.planId?.message && (
            <Typography
              color="error"
              variant="caption"
              className="font-Saira text-[11px] leading-[18px] font-medium italic text-left block text-[#EF5350] absolute"
            >
              {errors?.planId?.message as string}
            </Typography>
          )}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disableElevation
            className="normal-case font-Saira font-medium text-[10px] rounded-[8px] py-[8px] tracking-[0.6px] mt-[20px]"
            style={{ backgroundColor: themeColors?.primary, color: "#fff" }}
          >
            {t("submit")}
          </Button>
        </Box>
      )}
    </Box>
  );
};
